import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppInit } from '@rd/app-init/app-init.action';
import { getAuthIsLoggedIn } from '@rd/core/auth/store/selectors/auth.selectors';
import { ChangeLocale } from '@rd/core/locale/store/actions/locale.actions';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { UserPreferenceApiService } from '../../services/user-preference-api/user-preference-api.service';
import * as actions from '../actions/user-preference.actions';

@Injectable()
export class UserPreferenceEffects {
  constructor(
    private actions$: Actions,
    private userPreferenceApiService: UserPreferenceApiService,
    private store: Store,
  ) {}

  updatePointsAccountId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.UserPreferenceActionTypes.UPDATE_POINTS_ACCOUNT_ID),
      switchMap((action: actions.UpdatePointsAccountId) => {
        return this.userPreferenceApiService
          .updateUserPreference({
            pointsAccountId: action.payload,
          })
          .pipe(
            map(() => new actions.UpdatePointsAccountIdSuccess(action.payload)),
            catchError(() => of(new actions.UpdatePointsAccountIdFail())),
          );
      }),
    ),
  );

  updatedLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.UserPreferenceActionTypes.UPDATE_LOCALE),
      withLatestFrom(this.store.select(getAuthIsLoggedIn)),
      switchMap(
        ([action, isLoggedIn]: [actions.UpdateLocale, boolean | null]) => {
          if (isLoggedIn) {
            return this.userPreferenceApiService
              .updateUserPreference({
                locale: action.payload.code,
              })
              .pipe(
                map(() => new actions.UpdateLocaleSuccess(action.payload)),
                catchError(() => of(new actions.UpdateLocaleFail())),
              );
          }
          return EMPTY;
        },
      ),
    ),
  );

  updateLocalLocale$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.UserPreferenceActionTypes.UPDATE_LOCAL_STORAGE_LOCALE),
        tap((action: actions.UpdateLocalLocale) => {
          localStorage.setItem(
            'previous_locale',
            JSON.stringify(action.payload),
          );
        }),
      ),
    { dispatch: false },
  );

  restoreLocalLocale$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppInit),
        tap(() => {
          const previousLocale = JSON.parse(
            localStorage.getItem('previous_locale') ?? '{}',
          );
          if (!!Object.keys(previousLocale).length) {
            this.store.dispatch(new ChangeLocale(previousLocale));
          }
        }),
      ),
    { dispatch: false },
  );
}
